/** Focus guard for capture surfaces.
 *
 *  W015: the global Quick Capture surface auto-focuses its composer
 *  shortly after it opens (a deferred .focus() so the portal has
 *  painted). Two real-world ways that deferred focus stole input from
 *  the user:
 *
 *    1. Tab-blur race. The user opens capture, then switches to
 *       another tab/window before the deferred focus fires. The
 *       focus() yanks the browser tab back into the foreground input
 *       instead of leaving the user where they went.
 *    2. Another input already holds focus. The user is mid-typing in a
 *       search box / inline editor elsewhere when the capture surface
 *       wakes up; the deferred focus steals the caret out from under
 *       them and the next keystrokes land in the wrong field.
 *
 *  guardedFocus() encodes the rule from the W015 brief: only move
 *  focus to the capture surface when the tab itself is focused (pause
 *  on tab-blur) AND no editable element OUTSIDE the capture surface
 *  currently holds focus. Editable elements inside the surface (the
 *  trigger button, the composer's own chrome) do not block it. */

/** True when the browser tab/window is the active, focused document.
 *  Returns false while the tab is backgrounded so we pause instead of
 *  pulling focus back. */
export function isTabFocused(): boolean {
  if (typeof document === "undefined") return false;
  // document.hasFocus() is false when the tab is backgrounded or the
  // window lost focus; document.hidden covers the prerender/hidden
  // case some browsers report without a blur event.
  return document.hasFocus() && !document.hidden;
}

const EDITABLE_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

/** True when `el` is a text-entry surface the user could be typing in
 *  (input/textarea/select or a contenteditable host). */
function isEditable(el: Element | null): boolean {
  if (!el || !(el instanceof HTMLElement)) return false;
  if (EDITABLE_TAGS.has(el.tagName)) return true;
  return el.isContentEditable;
}

/** Move keyboard focus to `target`, but only when it is safe to do so:
 *  the tab is focused, and the active element is not an editable field
 *  living outside `surface`. Returns true when focus was applied.
 *
 *  `surface` is the capture surface root (e.g. the modal container).
 *  When the currently-focused element is inside that surface we never
 *  treat it as a steal, so re-focusing within the surface always
 *  works. */
export function guardedFocus(
  target: HTMLElement | null | undefined,
  surface?: HTMLElement | null,
): boolean {
  if (!target) return false;
  if (!isTabFocused()) return false;

  const active = (typeof document !== "undefined" ? document.activeElement : null) as
    | HTMLElement
    | null;

  // The body / null active element means nobody is typing; safe to
  // take focus.
  if (active && active !== document.body && isEditable(active)) {
    // An editable element holds focus. Only treat it as a steal when
    // it is outside the capture surface; focus already living inside
    // the surface is ours to move.
    const insideSurface = !!surface && surface.contains(active);
    if (!insideSurface) return false;
  }

  target.focus();
  return true;
}
